/**
 * Utility functions for converting filter values to API format
 */

import { DateFilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import type { TeachersReportLoginTimes } from '@/school-management/types/response/teacher';
import commonUtils from '@/utils/common';

/**
 * Convert date to ISO 8601 format
 * @param date - Date object
 * @returns ISO 8601 string
 */
export const toISO8601 = (date: Date): string => {
  return date.toISOString();
};

/**
 * Convert date filter to API format
 * @param filter - DateFilterValue from BimloaFilter
 * @returns API format with exact date or range, or null if no filter
 */
export const convertDateFilter = (
  filter: DateFilterValue
): { exact?: string; range?: { from: string; to: string } } | null => {
  if (!filter || filter.mode === 'none') return null;

  const now = new Date();
  now.setHours(23, 59, 59, 999); // End of today

  switch (filter.mode) {
    case 'last7': {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      from.setHours(0, 0, 0, 0);
      return { range: { from: toISO8601(from), to: toISO8601(now) } };
    }
    case 'last30': {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      from.setHours(0, 0, 0, 0);
      return { range: { from: toISO8601(from), to: toISO8601(now) } };
    }
    case 'specific': {
      if (filter.date) {
        const date = commonUtils.parseLocalDate(filter.date);
        date.setHours(0, 0, 0, 0);
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        return { range: { from: toISO8601(date), to: toISO8601(nextDay) } };
      }
      return null;
    }
    case 'range': {
      if (filter.from || filter.to) {
        const range = {
          range: {} as any,
        };

        if (filter.from) {
          const from = commonUtils.parseLocalDate(filter.from);
          from.setHours(0, 0, 0, 0);
          range.range.from = toISO8601(from);
        }

        if (filter.to) {
          const to = commonUtils.parseLocalDate(filter.to);
          to.setHours(23, 59, 59, 999);
          range.range.to = toISO8601(to);
        }

        return range;
      }
      return null;
    }
    default:
      return null;
  }
};

/**
 * Convert first/last login date filters to teachers report API loginTimes (from/to).
 */
export function dateFiltersToTeachersLoginTimes(
  firstLogin: unknown,
  lastLogin: unknown
): TeachersReportLoginTimes {
  const out: TeachersReportLoginTimes = {};
  const apply = (
    filter: unknown,
    exactKey: 'firstLoginExact' | 'lastLoginExact',
    rangeKey: 'firstLoginRange' | 'lastLoginRange'
  ) => {
    if (!filter || typeof filter !== 'object' || !('mode' in filter)) return;
    const mode = (filter as DateFilterValue).mode;
    if (!mode || mode === 'none') return;
    const converted = convertDateFilter(filter as DateFilterValue);
    if (!converted) return;
    if (converted.exact) out[exactKey] = converted.exact;
    if (converted.range) out[rangeKey] = { from: converted.range.from, to: converted.range.to };
  };
  apply(firstLogin, 'firstLoginExact', 'firstLoginRange');
  apply(lastLogin, 'lastLoginExact', 'lastLoginRange');
  return out;
}

/**
 * Convert array of number strings to min/max range
 * @param arr - Array of string numbers (e.g., ["1", "2", "3"])
 * @returns Object with min and max, or null if array is empty or has single value
 */
export const arrayToRange = (arr: string[]): { min: number; max: number } | null => {
  const numbers = arr
    .filter(v => v && v !== 'All')
    .map(v => Number(v))
    .filter(v => !isNaN(v));

  if (numbers.length === 0) return null;
  if (numbers.length === 1) {
    // Single value - use exact match instead of range
    return null;
  }

  return {
    min: Math.min(...numbers),
    max: Math.max(...numbers),
  };
};

/**
 * Parse time string in "Xh Ym" format to seconds
 * @param timeStr - Time string (e.g., "1h 30m", "2h 0m")
 * @returns Seconds as number, or null if format is invalid
 */
export const parseTimeToSeconds = (timeStr: string): number | null => {
  const match = timeStr.match(/(\d+)h\s*(\d+)m/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    return hours * 3600 + minutes * 60;
  }
  return null;
};

/**
 * Convert array of time strings to min/max range in seconds
 * @param arr - Array of time strings (e.g., ["1h 30m", "2h 0m"])
 * @returns Object with min and max in seconds, or null if array is empty or has single value
 */
export const timeSpentArrayToRange = (arr: string[]): { min: number; max: number } | null => {
  const seconds = arr
    .filter(v => v && v !== 'All')
    .map(v => parseTimeToSeconds(v))
    .filter((v): v is number => v !== null);

  if (seconds.length === 0) return null;
  if (seconds.length === 1) {
    return null; // Single value - use exact match instead
  }

  return {
    min: Math.min(...seconds),
    max: Math.max(...seconds),
  };
};
